import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import emailjs from '@emailjs/browser';
import {
  Box,
  Fab,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  Collapse,
  Badge,
  Tooltip,
  Button,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  DesignServices as DesignIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Build as BuildIcon,
  AddHome as AddHomeIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  SkipNext as SkipNextIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as QuestionAnswerIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";
import {
  toggleChat,
  updateFormData,
  nextQuestion,
  prevQuestion,
  skipQuestion,
  submitForm,
  resetForm,
  changeQuestionSet,
  clearAllData,
  loadSubmissionsFromStorage,
} from "../../store/chatSlice";
import {
  selectChatIsOpen,
  selectChatMessages,
  selectShowEmailForm,
  selectFormData,
  selectCurrentQuestion,
  selectCurrentQuestionIndex,
  selectCurrentQuestionSet,
  selectProgressPercentage,
  selectCollectedData,
  selectSubmissions,
  selectSubmissionStats,
  selectAllQuestionSets,
} from "../../store/chatSelectors";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Color Constants
const PRIMARY_COLOR = "#2b2b3a"; 
const SECONDARY_COLOR = "#bec99b"; // Green
const SUCCESS_COLOR = "#bec99b"; // Green for success states
const ERROR_COLOR = "#f44336"; // Keep standard red for errors
const BACKGROUND_COLOR = "#f5f5f5"; // Light background
const TEXT_COLOR = "#333333"; // Dark text

// EmailJS Configuration
const EMAILJS_CONFIG = {
   SERVICE_ID: "service_a8t9sbt",
   TEMPLATE_ID: "template_kkd9iri",
   PUBLIC_KEY: "PinX8mgdGG_GoQx52",
  TO_NAME: "Decodia",
  TO_EMAIL: "info@decodia.in"
};

// Styled Components
const StyledChatContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  zIndex: 1000,
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    left: theme.spacing(1),
  },
}));

const StyledChatBox = styled(Paper)(({ theme }) => ({
  width: 450,
  maxHeight: '80vh',
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: `0 8px 32px rgba(43, 43, 58, 0.2)`,
  borderRadius: 16,
  border: `1px solid ${SECONDARY_COLOR}40`,
  background: `linear-gradient(135deg, ${BACKGROUND_COLOR} 0%, #ffffff 100%)`,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    maxHeight: '90vh',
    borderRadius: 12,
  },
  [theme.breakpoints.down('xs')]: {
    maxHeight: '85vh',
    borderRadius: 8,
  },
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, #3a3a4d 100%)`,
  color: "white",
  padding: theme.spacing(2),
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  borderBottom: `2px solid ${SECONDARY_COLOR}`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
}));

const MessageList = styled(List)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: 8,
  maxHeight: 'calc(80vh - 200px)',
  [theme.breakpoints.down('sm')]: {
    maxHeight: 'calc(90vh - 180px)',
    padding: 4,
  },
  [theme.breakpoints.down('xs')]: {
    maxHeight: 'calc(85vh - 160px)',
  },
}));

const FormSection = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: `${SECONDARY_COLOR}15`,
  border: `1px solid ${SECONDARY_COLOR}40`,
  borderRadius: 12,
  backdropFilter: "blur(10px)",
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0.5),
    padding: theme.spacing(1.5),
    borderRadius: 8,
  },
}));

// Brand Philosophy
const BRAND_PHILOSOPHY = "Designs that speak the language of your heart. Be unique.";

// FAQ Data from PDF Document
const faqData = {
  residential: {
    name: "Residential Design",
    icon: <HomeIcon />,
    questions: [
      {
        id: "res_q1",
        question: "Do you design apartments, villas, or independent houses?",
        answer: "Yes. We design apartments, villas, independent houses, and builder floors---each tailored to your lifestyle and family needs."
      },
      {
        id: "res_q2",
        question: "I don't have a clear idea of design. Can you still help?",
        answer: "Absolutely. Most homeowners start this way. We understand your routines, preferences, and emotions to create a home that truly feels like you."
      },
      {
        id: "res_q3",
        question: "Will my home look exactly like the 3D design?",
        answer: "The final output matches the approved design concept, with real-life material textures and finishes carefully executed."
      },
      {
        id: "res_q4",
        question: "Can I stay in my home while the work is going on?",
        answer: "In most cases, yes. We plan execution phase-wise to minimise disturbance."
      },
      {
        id: "res_q5",
        question: "How do you ensure my home doesn't look generic?",
        answer: "We never repeat designs. Every layout, colour, and detail is customised around your personality and lifestyle."
      }
    ]
  },
  commercial: {
    name: "Commercial Design",
    icon: <BusinessIcon />,
    questions: [
      {
        id: "com_q1",
        question: "What commercial spaces do you design?",
        answer: "Offices, retail stores, showrooms, cafes, clinics, salons, and studios."
      },
      {
        id: "com_q2",
        question: "Can you design according to my brand identity?",
        answer: "Yes. We design spaces that reflect your brand values, customer psychology, and business goals."
      },
      {
        id: "com_q3",
        question: "Will the design help improve productivity or customer experience?",
        answer: "Definitely. Good commercial design improves workflow, comfort, and brand perception."
      },
      {
        id: "com_q4",
        question: "Can you work with tight deadlines?",
        answer: "Yes. Commercial projects are planned with strict timelines and efficient execution strategies."
      },
      {
        id: "com_q5",
        question: "Do you manage vendors and contractors?",
        answer: "Yes. We coordinate with vendors, suppliers, and site teams so you can focus on your business."
      }
    ]
  },
  renovation: {
    name: "Renovation Projects",
    icon: <BuildIcon />,
    questions: [
      {
        id: "ren_q1",
        question: "Can you renovate only part of my home?",
        answer: "Yes. Kitchens, bedrooms, living rooms, bathrooms, or selective upgrades---we do partial and full renovations."
      },
      {
        id: "ren_q2",
        question: "Will renovation cost more than new interiors?",
        answer: "Renovation requires careful planning, but smart design decisions help control costs effectively."
      },
      {
        id: "ren_q3",
        question: "How do you handle existing furniture or structures?",
        answer: "We evaluate what can be reused, upgraded, or redesigned to save cost and maintain emotional value."
      },
      {
        id: "ren_q4",
        question: "Is renovation messy and stressful?",
        answer: "It doesn't have to be. We plan clean execution, clear timelines, and regular updates to reduce stress."
      },
      {
        id: "ren_q5",
        question: "Can you modernise my old home without breaking everything?",
        answer: "Yes. We focus on smart upgrades---lighting, finishes, layouts---without unnecessary demolition."
      }
    ]
  },
  new_project: {
    name: "New Projects",
    icon: <AddHomeIcon />,
    questions: [
      {
        id: "new_q1",
        question: "When should I contact you for a new project?",
        answer: "Ideally before construction begins. Early planning avoids costly changes later."
      },
      {
        id: "new_q2",
        question: "Do you work with architects and builders?",
        answer: "Yes. We collaborate closely to ensure design, structure, and execution align perfectly."
      },
      {
        id: "new_q3",
        question: "Can you help with space planning from scratch?",
        answer: "Absolutely. We help design layouts that maximise space, light, and functionality from day one."
      },
      {
        id: "new_q4",
        question: "Will early interior planning save money?",
        answer: "Yes. Early design decisions prevent rework, material wastage, and budget overruns."
      },
      {
        id: "new_q5",
        question: "Can you design future-ready homes?",
        answer: "Yes. We consider flexibility, storage, technology, and long-term comfort while designing."
      }
    ]
  }
};

// Project Type Options for Free Consultation
const projectTypes = [
  { value: "residential", label: "Residential Design", icon: <HomeIcon /> },
  { value: "commercial", label: "Commercial Design", icon: <BusinessIcon /> },
  { value: "renovation", label: "Renovation Projects", icon: <BuildIcon /> },
  { value: "new_project", label: "New Projects", icon: <AddHomeIcon /> },
];

// Section-wise questions for each project type (Free Consultation)
const projectQuestions = {
  residential: [
    {
      id: "res_name",
      type: "text",
      field: "name",
      question: "What's your name?",
      required: true,
      placeholder: "Enter your full name"
    },
    {
      id: "res_email",
      type: "email",
      field: "email",
      question: "What's your email address?",
      required: true,
      placeholder: "Enter your email"
    },
    {
      id: "res_contact",
      type: "phone",
      field: "phone",
      question: "What's your phone number?",
      required: true,
      placeholder: "Enter your phone number"
    },
    {
      id: "res_property_type",
      type: "single-select",
      field: "property_type",
      question: "What type of residential property do you have?",
      required: true,
      options: ["Apartment", "Villa", "Independent House", "Builder Floor", "Penthouse", "Other"]
    },
    {
      id: "res_area",
      type: "text",
      field: "area",
      question: "What is the area of your property? (in sq.ft.)",
      required: true,
      placeholder: "e.g., 1200 sq.ft."
    },
    {
      id: "res_rooms",
      type: "multi-select",
      field: "rooms_to_design",
      question: "Which rooms would you like to design?",
      required: true,
      options: ["Living Room", "Bedroom", "Kitchen", "Bathroom", "Dining Room", "Study/Office", "Balcony", "Entrance Foyer"]
    },
    {
      id: "res_style",
      type: "single-select",
      field: "design_style",
      question: "What design style do you prefer?",
      required: false,
      options: ["Modern", "Contemporary", "Traditional", "Minimalist", "Industrial", "Scandinavian", "Bohemian", "Not sure yet"]
    },
    {
      id: "res_budget",
      type: "single-select",
      field: "budget",
      question: "What is your approximate budget range?",
      required: true,
      options: ["Under 5 lakhs", "5-10 lakhs", "10-20 lakhs", "20-50 lakhs", "50+ lakhs"]
    },
    {
      id: "res_timeline",
      type: "single-select",
      field: "timeline",
      question: "What is your project timeline?",
      required: true,
      options: ["Immediately", "1-3 months", "3-6 months", "6+ months"]
    },
    {
      id: "res_specifics",
      type: "textarea",
      field: "specific_requirements",
      question: "Any specific requirements, preferences, or challenges with your space?",
      required: false,
      placeholder: "Tell us about your family needs, lifestyle, or any specific requirements..."
    }
  ],
  commercial: [
    {
      id: "com_name",
      type: "text",
      field: "name",
      question: "What's your name?",
      required: true,
      placeholder: "Enter your full name"
    },
    {
      id: "com_email",
      type: "email",
      field: "email",
      question: "What's your email address?",
      required: true,
      placeholder: "Enter your email"
    },
    {
      id: "com_business",
      type: "text",
      field: "business_name",
      question: "What's your business name?",
      required: true,
      placeholder: "Enter your business/company name"
    },
    {
      id: "com_contact",
      type: "phone",
      field: "phone",
      question: "What's your phone number?",
      required: true,
      placeholder: "Enter your phone number"
    },
    {
      id: "com_type",
      type: "single-select",
      field: "commercial_type",
      question: "What type of commercial space?",
      required: true,
      options: ["Office", "Retail Store", "Restaurant/Cafe", "Showroom", "Clinic", "Salon/Spa", "Hotel", "Other"]
    },
    {
      id: "com_area",
      type: "text",
      field: "area",
      question: "What is the area of your commercial space? (in sq.ft.)",
      required: true,
      placeholder: "e.g., 2000 sq.ft."
    },
    {
      id: "com_capacity",
      type: "text",
      field: "capacity",
      question: "What is the expected capacity? (employees/customers)",
      required: true,
      placeholder: "e.g., 50 employees or 100 customers"
    },
    {
      id: "com_branding",
      type: "textarea",
      field: "brand_info",
      question: "Tell us about your brand identity, colors, or specific requirements",
      required: false,
      placeholder: "Share your brand values, colors, or any specific requirements..."
    },
    {
      id: "com_budget",
      type: "single-select",
      field: "budget",
      question: "What is your approximate budget range?",
      required: true,
      options: ["Under 10 lakhs", "10-25 lakhs", "25-50 lakhs", "50-100 lakhs", "100+ lakhs"]
    },
    {
      id: "com_timeline",
      type: "single-select",
      field: "timeline",
      question: "What is your project timeline?",
      required: true,
      options: ["Urgent (within 1 month)", "1-3 months", "3-6 months", "6+ months"]
    }
  ],
  renovation: [
    {
      id: "ren_name",
      type: "text",
      field: "name",
      question: "What's your name?",
      required: true,
      placeholder: "Enter your full name"
    },
    {
      id: "ren_email",
      type: "email",
      field: "email",
      question: "What's your email address?",
      required: true,
      placeholder: "Enter your email"
    },
    {
      id: "ren_contact",
      type: "phone",
      field: "phone",
      question: "What's your phone number?",
      required: true,
      placeholder: "Enter your phone number"
    },
    {
      id: "ren_property_type",
      type: "single-select",
      field: "property_type",
      question: "What type of property are you renovating?",
      required: true,
      options: ["Apartment", "Villa", "Independent House", "Office", "Retail Space", "Other"]
    },
    {
      id: "ren_area",
      type: "text",
      field: "area",
      question: "What is the area to be renovated? (in sq.ft.)",
      required: true,
      placeholder: "e.g., 800 sq.ft."
    },
    {
      id: "ren_scope",
      type: "multi-select",
      field: "renovation_scope",
      question: "What areas need renovation?",
      required: true,
      options: ["Complete Home", "Kitchen", "Bedroom", "Living Room", "Bathroom", "Exterior", "Flooring", "Electrical & Plumbing", "Painting"]
    },
    {
      id: "ren_age",
      type: "single-select",
      field: "property_age",
      question: "How old is the property?",
      required: true,
      options: ["Less than 5 years", "5-10 years", "10-20 years", "20+ years"]
    },
    {
      id: "ren_challenges",
      type: "textarea",
      field: "challenges",
      question: "What are the main challenges or issues you're facing with the current space?",
      required: false,
      placeholder: "e.g., space utilization, outdated design, structural issues, etc."
    },
    {
      id: "ren_budget",
      type: "single-select",
      field: "budget",
      question: "What is your approximate budget for renovation?",
      required: true,
      options: ["Under 3 lakhs", "3-5 lakhs", "5-10 lakhs", "10-20 lakhs", "20+ lakhs"]
    },
    {
      id: "ren_timeline",
      type: "single-select",
      field: "timeline",
      question: "What is your project timeline?",
      required: true,
      options: ["Immediately", "1-3 months", "3-6 months", "6+ months"]
    }
  ],
  new_project: [
    {
      id: "new_name",
      type: "text",
      field: "name",
      question: "What's your name?",
      required: true,
      placeholder: "Enter your full name"
    },
    {
      id: "new_email",
      type: "email",
      field: "email",
      question: "What's your email address?",
      required: true,
      placeholder: "Enter your email"
    },
    {
      id: "new_contact",
      type: "phone",
      field: "phone",
      question: "What's your phone number?",
      required: true,
      placeholder: "Enter your phone number"
    },
    {
      id: "new_property_type",
      type: "single-select",
      field: "property_type",
      question: "What type of new property?",
      required: true,
      options: ["Apartment", "Villa", "Independent House", "Commercial Building", "Mixed Use"]
    },
    {
      id: "new_area",
      type: "text",
      field: "area",
      question: "What is the total area? (in sq.ft.)",
      required: true,
      placeholder: "e.g., 2500 sq.ft."
    },
    {
      id: "new_stage",
      type: "single-select",
      field: "project_stage",
      question: "What is the current stage of your project?",
      required: true,
      options: ["Planning Stage", "Under Construction", "Recently Purchased", "Land Available"]
    },
    {
      id: "new_rooms",
      type: "multi-select",
      field: "required_rooms",
      question: "What rooms/spaces do you need?",
      required: true,
      options: ["Living Room", "Bedrooms", "Kitchen", "Bathrooms", "Dining Area", "Study", "Home Theater", "Gym", "Guest Room", "Storage"]
    },
    {
      id: "new_family",
      type: "text",
      field: "family_size",
      question: "How many family members will live here?",
      required: true,
      placeholder: "e.g., 4 members (2 adults, 2 children)"
    },
    {
      id: "new_features",
      type: "textarea",
      field: "desired_features",
      question: "What specific features or amenities do you want?",
      required: false,
      placeholder: "e.g., smart home features, sustainability features, special requirements..."
    },
    {
      id: "new_budget",
      type: "single-select",
      field: "budget",
      question: "What is your approximate budget range?",
      required: true,
      options: ["Under 20 lakhs", "20-50 lakhs", "50-100 lakhs", "100-200 lakhs", "200+ lakhs"]
    },
    {
      id: "new_timeline",
      type: "single-select",
      field: "timeline",
      question: "When do you plan to start construction?",
      required: true,
      options: ["Immediately", "1-3 months", "3-6 months", "6-12 months", "1+ year"]
    }
  ]
};

// Helper function to format form data for email
const formatFormDataForEmail = (formData) => {
  let message = `NEW CONSULTATION REQUEST\n`;
  message += `================================\n\n`;
  
  message += `📋 PROJECT DETAILS\n`;
  message += `-----------------\n`;
  message += `Project Type: ${formData.project_type_label || 'Not specified'}\n`;
  message += `Submitted: ${new Date(formData.submitted_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}\n\n`;
  
  message += `👤 CONTACT INFORMATION\n`;
  message += `----------------------\n`;
  message += `Name: ${formData.name || 'Not provided'}\n`;
  message += `Email: ${formData.email || 'Not provided'}\n`;
  message += `Phone: ${formData.phone || 'Not provided'}\n\n`;
  
  // Add specific project details
  message += `🏠 PROJECT SPECIFICS\n`;
  message += `-------------------\n`;
  
  // Filter out system fields and contact info
  const systemFields = ['submitted_at', 'project_type', 'project_type_label', 'name', 'email', 'phone', 'id'];
  Object.entries(formData).forEach(([key, value]) => {
    if (!systemFields.includes(key)) {
      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      if (Array.isArray(value)) {
        message += `${formattedKey}: ${value.join(', ')}\n`;
      } else if (typeof value === 'string' && value.length > 0) {
        message += `${formattedKey}: ${value}\n`;
      } else if (typeof value === 'boolean') {
        message += `${formattedKey}: ${value ? 'Yes' : 'No'}\n`;
      } else if (value !== null && value !== undefined) {
        message += `${formattedKey}: ${value}\n`;
      }
    }
  });
  
  message += `\n📝 SOURCE: DECODIA Chat Assistant`;
  
  return message;
};

// Helper function to create fallback mailto link
const createFallbackMailtoLink = (formData) => {
  const subject = encodeURIComponent(`New Consultation Request: ${formData.project_type_label || 'Design Consultation'}`);
  
  let body = `Dear Dipanita,\n\n`;
  body += `I am interested in a design consultation for my project. Here are my details:\n\n`;
  body += `PROJECT TYPE: ${formData.project_type_label || 'Not specified'}\n`;
  body += `NAME: ${formData.name || 'Not provided'}\n`;
  body += `EMAIL: ${formData.email || 'Not provided'}\n`;
  body += `PHONE: ${formData.phone || 'Not provided'}\n\n`;
  
  // Add project details
  body += `PROJECT DETAILS:\n`;
  const systemFields = ['submitted_at', 'project_type', 'project_type_label', 'name', 'email', 'phone', 'id'];
  Object.entries(formData).forEach(([key, value]) => {
    if (!systemFields.includes(key)) {
      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      if (Array.isArray(value)) {
        body += `${formattedKey}: ${value.join(', ')}\n`;
      } else if (typeof value === 'string' && value.length > 0) {
        body += `${formattedKey}: ${value}\n`;
      } else if (typeof value === 'boolean') {
        body += `${formattedKey}: ${value ? 'Yes' : 'No'}\n`;
      } else if (value !== null && value !== undefined) {
        body += `${formattedKey}: ${value}\n`;
      }
    }
  });
  
  body += `\nBest regards,\n${formData.name || 'Customer'}`;
  
  return `mailto:${EMAILJS_CONFIG.TO_EMAIL}?subject=${subject}&body=${encodeURIComponent(body)}`;
};

export default function ChatButton() {
  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [showPreview, setShowPreview] = useState(true);
  const [activeFaqCategory, setActiveFaqCategory] = useState("residential");
  const [mode, setMode] = useState("faq"); // 'faq' or 'consultation'
  const [selectedProjectType, setSelectedProjectType] = useState("");
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  
  // FAQ Contact Form State
  const [faqContactForm, setFaqContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    question: "",
    faqCategory: "residential"
  });
  const [isFaqSubmitting, setIsFaqSubmitting] = useState(false);

  // Selectors
  const isOpen = useSelector(selectChatIsOpen);
  const submissions = useSelector(selectSubmissions);

  // Load submissions from localStorage on mount
  useEffect(() => {
    dispatch(loadSubmissionsFromStorage());
  }, [dispatch]);

  // Scroll to bottom when mode changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [mode, selectedProjectType]);

  // Load questions when project type is selected
  useEffect(() => {
    if (selectedProjectType && projectQuestions[selectedProjectType]) {
      setCurrentQuestions(projectQuestions[selectedProjectType]);
      setAnswers({});
      setTouchedFields({});
    }
  }, [selectedProjectType]);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
    
    setTouchedFields(prev => ({
      ...prev,
      [questionId]: true
    }));
  };

  const handleOptionToggle = (questionId, option, isMultiSelect) => {
    const currentAnswers = answers[questionId] || [];
    
    if (isMultiSelect) {
      const newOptions = Array.isArray(currentAnswers) && currentAnswers.includes(option)
        ? currentAnswers.filter((item) => item !== option)
        : [...(Array.isArray(currentAnswers) ? currentAnswers : []), option];
      
      handleInputChange(questionId, newOptions);
    } else {
      handleInputChange(questionId, option);
    }
  };

  // Function to send consultation email
  const sendConsultationEmail = async (formData) => {
    try {
      // Prepare email template parameters
      const templateParams = {
        to_name: EMAILJS_CONFIG.TO_NAME,
        to_email: EMAILJS_CONFIG.TO_EMAIL,
        from_name: formData.name || "Customer",
        from_email: formData.email || "Not provided",
        from_phone: formData.phone || "Not provided",
        project_type: formData.project_type_label || "Not specified",
        company: formData.business_name || formData.company || "Not provided",
        department: "Consultation Request",
        subject: `New Consultation Request: ${formData.project_type_label}`,
        message: formatFormDataForEmail(formData),
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        site: "DECODIA Chat Assistant",
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      return { success: true, response };
      
    } catch (error) {
      let errorMessage = "Email service error";
      if (error.text) errorMessage = error.text;
      else if (error.message) errorMessage = error.message;
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Function to send FAQ question email
  const sendFaqQuestionEmail = async (formData) => {
    try {
      // Prepare email template parameters for FAQ questions
      const templateParams = {
        to_name: EMAILJS_CONFIG.TO_NAME,
        to_email: EMAILJS_CONFIG.TO_EMAIL,
        from_name: formData.name || "Customer",
        from_email: formData.email || "Not provided",
        from_phone: formData.phone || "Not provided",
        project_type: "FAQ Question",
        company: "Not provided",
        department: "FAQ Inquiry",
        subject: `FAQ Question: ${formData.faq_category_label}`,
        message: `FAQ CATEGORY: ${formData.faq_category_label}\n\nQUESTION:\n${formData.question}\n\nCONTACT INFO:\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nSubmitted via DECODIA Chat Assistant`,
        date: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        site: "DECODIA Chat Assistant - FAQ",
      };
      const response = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );
      
      return { success: true, response };
      
    } catch (error) {
      console.error("❌ Failed to send FAQ email:", error);
      
      let errorMessage = "Email service error";
      if (error.text) errorMessage = error.text;
      else if (error.message) errorMessage = error.message;
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const handleSubmitForm = async () => {
    if (!selectedProjectType) return;
    
    // Validate required fields
    const requiredQuestions = currentQuestions.filter(q => q.required);
    const missingRequired = requiredQuestions.filter(q => {
      const answer = answers[q.id];
      return !answer || (Array.isArray(answer) && answer.length === 0);
    });
    
    if (missingRequired.length > 0) {
      showSnackbar(`Please fill all required fields: ${missingRequired.map(q => q.field).join(', ')}`, "warning");
      return;
    }
    
    setIsSubmitting(true);
    
    // Prepare form data
    const formData = {
      project_type: selectedProjectType,
      project_type_label: projectTypes.find(p => p.value === selectedProjectType)?.label || selectedProjectType,
      ...answers,
      submitted_at: new Date().toISOString(),
    };

    try {
      // First, send email using EmailJS
      const emailResult = await sendConsultationEmail(formData);
      
      if (!emailResult.success) {
        throw new Error(emailResult.error || "Failed to send email");
      }
      
      // Then dispatch to Redux store
      dispatch(submitForm(formData));
      showSnackbar("Consultation request submitted successfully! We'll contact you soon.", "success");
      
      // Reset form
      setTimeout(() => {
        setSelectedProjectType("");
        setCurrentQuestions([]);
        setAnswers({});
        setTouchedFields({});
        setIsSubmitting(false);
        setMode("faq");
      }, 2000);
    } catch (error) {
      console.error("Failed to submit form:", error);
      
      // Try fallback - open mail client
      const shouldUseFallback = window.confirm(
        "Email service failed. Would you like to open your email client instead?"
      );
      
      if (shouldUseFallback) {
        const mailtoLink = createFallbackMailtoLink(formData);
        window.location.href = mailtoLink;
        
        // Still save to Redux store
        dispatch(submitForm(formData));
        showSnackbar("Form saved locally. Please check your email client.", "info");
        
        setTimeout(() => {
          setSelectedProjectType("");
          setCurrentQuestions([]);
          setAnswers({});
          setTouchedFields({});
          setIsSubmitting(false);
        }, 2000);
      } else {
        showSnackbar("Failed to submit form. Please try again.", "error");
        setIsSubmitting(false);
      }
    }
  };

  const handleFaqContactSubmit = async () => {
    const { name, email, phone, question, faqCategory } = faqContactForm;
    
    // Validate
    if (!name.trim() || !email.trim() || !phone.trim() || !question.trim()) {
      showSnackbar("Please fill all fields", "warning");
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showSnackbar("Please enter a valid email address", "warning");
      return;
    }
    
    // Phone validation (basic)
    const phoneRegex = /^[0-9+\-\s]+$/;
    if (!phoneRegex.test(phone) || phone.length < 10) {
      showSnackbar("Please enter a valid phone number", "warning");
      return;
    }
    
    setIsFaqSubmitting(true);
    
    // Prepare FAQ contact data
    const faqContactData = {
      type: "faq_question",
      faq_category: faqCategory,
      faq_category_label: faqData[faqCategory].name,
      name,
      email,
      phone,
      question,
      submitted_at: new Date().toISOString(),
    };
    
    try {
      // Send email using EmailJS
      const emailResult = await sendFaqQuestionEmail(faqContactData);
      
      if (!emailResult.success) {
        throw new Error(emailResult.error || "Failed to send email");
      }
      
      // Dispatch to Redux store
      dispatch(submitForm(faqContactData));
      showSnackbar("Your question has been submitted! We'll get back to you soon.", "success");
      
      // Reset FAQ contact form
      setTimeout(() => {
        setFaqContactForm({
          name: "",
          email: "",
          phone: "",
          question: "",
          faqCategory: activeFaqCategory
        });
        setIsFaqSubmitting(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to submit FAQ question:", error);
      
      // Try fallback for FAQ questions too
      const shouldUseFallback = window.confirm(
        "Email service failed. Would you like to open your email client instead?"
      );
      
      if (shouldUseFallback) {
        const subject = encodeURIComponent(`FAQ Question: ${faqData[faqCategory].name}`);
        const body = encodeURIComponent(
          `FAQ Category: ${faqData[faqCategory].name}\n\nMy Question:\n${question}\n\nContact Information:\nName: ${name}\nEmail: ${email}\nPhone: ${phone}\n\nSent via DECODIA Chat Assistant`
        );
        const mailtoLink = `mailto:${EMAILJS_CONFIG.TO_EMAIL}?subject=${subject}&body=${body}`;
        window.location.href = mailtoLink;
        
        // Still save to Redux store
        dispatch(submitForm(faqContactData));
        showSnackbar("Question saved locally. Please check your email client.", "info");
        
        setTimeout(() => {
          setFaqContactForm({
            name: "",
            email: "",
            phone: "",
            question: "",
            faqCategory: activeFaqCategory
          });
          setIsFaqSubmitting(false);
        }, 2000);
      } else {
        showSnackbar("Failed to submit question. Please try again.", "error");
        setIsFaqSubmitting(false);
      }
    }
  };

  const handleClearData = () => {
    if (window.confirm("Are you sure you want to clear all saved data?")) {
      dispatch(clearAllData());
      showSnackbar("All data cleared successfully", "info");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const renderQuestionInput = (question) => {
    const { type, options, placeholder, field, id, required } = question;
    const value = answers[id] || '';
    const touched = touchedFields[id] || false;
    const showError = touched && required && (!value || (Array.isArray(value) && value.length === 0));

    const renderInput = () => {
      switch (type) {
        case "text":
        case "phone":
        case "email":
        case "number":
          return (
            <TextField
              fullWidth
              type={type === "phone" ? "tel" : type === "email" ? "email" : type}
              value={value}
              onChange={(e) => handleInputChange(id, e.target.value)}
              placeholder={placeholder || `Enter your ${field.replace("_", " ")}`}
              variant="outlined"
              size="small"
              error={showError}
              helperText={showError ? "This field is required" : ""}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: SECONDARY_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: PRIMARY_COLOR,
                  },
                },
                mt: 0.5,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {type === "phone" ? <PhoneIcon /> : 
                     type === "email" ? <EmailIcon /> : 
                     <PersonIcon />}
                  </InputAdornment>
                ),
              }}
            />
          );

        case "textarea":
          return (
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 3 : 4}
              value={value}
              onChange={(e) => handleInputChange(id, e.target.value)}
              placeholder={placeholder || "Enter your comments..."}
              variant="outlined"
              size="small"
              error={showError}
              helperText={showError ? "This field is required" : ""}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: SECONDARY_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: PRIMARY_COLOR,
                  },
                },
                mt: 0.5,
              }}
            />
          );

        case "multi-select":
          return (
            <FormGroup>
              <Box sx={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: 0.5,
                maxHeight: isMobile ? '150px' : '200px',
                overflowY: 'auto',
                p: 0.5,
                mt: 0.5,
              }}>
                {options?.map((option) => (
                  <Chip
                    key={option}
                    label={option}
                    onClick={() => handleOptionToggle(id, option, true)}
                    sx={{
                      m: 0.25,
                      backgroundColor: Array.isArray(value) && value.includes(option)
                        ? `${PRIMARY_COLOR} !important`
                        : `${SECONDARY_COLOR}20 !important`,
                      color: Array.isArray(value) && value.includes(option)
                        ? 'white !important'
                        : TEXT_COLOR,
                      borderColor: Array.isArray(value) && value.includes(option)
                        ? PRIMARY_COLOR
                        : SECONDARY_COLOR,
                      '&:hover': {
                        backgroundColor: Array.isArray(value) && value.includes(option)
                          ? `${PRIMARY_COLOR}cc !important`
                          : `${SECONDARY_COLOR}40 !important`,
                      },
                      fontSize: isMobile ? '0.75rem' : '0.875rem',
                    }}
                    size={isMobile ? "small" : "medium"}
                    clickable
                  />
                ))}
              </Box>
              {showError && (
                <Typography
                  variant="caption"
                  sx={{ 
                    color: 'error.main',
                    mt: 0.5,
                    display: 'block',
                  }}
                >
                  Please select at least one option
                </Typography>
              )}
              {Array.isArray(value) && value.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ 
                    mt: 0.5,
                    display: 'block',
                    color: TEXT_COLOR,
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                  }}
                >
                  Selected: {value.join(", ")}
                </Typography>
              )}
            </FormGroup>
          );

        case "single-select":
          return (
            <FormControl component="fieldset" sx={{ width: "100%", mt: 0.5 }}>
              <RadioGroup
                value={value}
                onChange={(e) => handleOptionToggle(id, e.target.value, false)}
              >
                {options?.map((option) => (
                  <FormControlLabel
                    key={option}
                    value={option}
                    control={
                      <Radio 
                        size="small" 
                        sx={{
                          color: SECONDARY_COLOR,
                          '&.Mui-checked': {
                            color: PRIMARY_COLOR,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ 
                        fontSize: isMobile ? '0.8rem' : '0.875rem',
                        color: TEXT_COLOR,
                      }}>
                        {option}
                      </Typography>
                    }
                    sx={{ 
                      mb: 0.5,
                      '& .MuiFormControlLabel-label': {
                        color: TEXT_COLOR,
                      },
                    }}
                  />
                ))}
              </RadioGroup>
              {showError && (
                <Typography
                  variant="caption"
                  sx={{ 
                    color: 'error.main',
                    mt: 0.5,
                    display: 'block',
                  }}
                >
                  Please select an option
                </Typography>
              )}
            </FormControl>
          );

        default:
          return null;
      }
    };

    return (
      <Box sx={{ mb: 2.5 }}>
        <Typography 
          variant="body2" 
          fontWeight={600} 
          sx={{ 
            color: PRIMARY_COLOR,
            mb: 0.5,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}
        >
          {question.question}
          {required && (
            <Typography component="span" color="error" sx={{ ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
        {renderInput()}
      </Box>
    );
  };

  const renderFaqSection = () => {
    const category = faqData[activeFaqCategory];
    
    return (
      <Box sx={{ p: isMobile ? 1.5 : 2 }}>
        {/* Brand Philosophy */}
        <Box sx={{ 
          mb: isMobile ? 2 : 3, 
          p: isMobile ? 1.5 : 2, 
          borderRadius: 2, 
          backgroundColor: `${PRIMARY_COLOR}10`,
          borderLeft: `4px solid ${SECONDARY_COLOR}`,
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontStyle: 'italic',
              color: PRIMARY_COLOR,
              fontWeight: 500,
              fontSize: isMobile ? '0.9rem' : '1rem',
            }}
          >
            "{BRAND_PHILOSOPHY}"
          </Typography>
        </Box>

        {/* FAQ Category Selector */}
        <Typography 
          variant={isMobile ? "subtitle2" : "subtitle1"} 
          fontWeight={600} 
          sx={{ color: PRIMARY_COLOR, mb: 2 }}
        >
          Browse FAQs by Category
        </Typography>
        <Box sx={{ 
          display: "flex", 
          gap: 0.75, 
          mb: 3, 
          flexWrap: "wrap",
          justifyContent: isMobile ? 'center' : 'flex-start',
        }}>
          {Object.keys(faqData).map((key) => (
            <Chip
              key={key}
              icon={faqData[key].icon}
              label={
                <Typography sx={{ 
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  fontWeight: 500,
                }}>
                  {isMobile && faqData[key].name.split(' ')[0]}
                  {!isMobile && faqData[key].name}
                </Typography>
              }
              onClick={() => {
                setActiveFaqCategory(key);
                setFaqContactForm(prev => ({ ...prev, faqCategory: key }));
              }}
              sx={{
                backgroundColor: activeFaqCategory === key 
                  ? `${PRIMARY_COLOR} !important`
                  : `${SECONDARY_COLOR}20 !important`,
                color: activeFaqCategory === key 
                  ? 'white !important'
                  : TEXT_COLOR,
                borderColor: activeFaqCategory === key
                  ? PRIMARY_COLOR
                  : SECONDARY_COLOR,
                '&:hover': {
                  backgroundColor: activeFaqCategory === key
                    ? `${PRIMARY_COLOR}cc !important`
                    : `${SECONDARY_COLOR}40 !important`,
                },
                minWidth: isMobile ? '80px' : 'auto',
                height: isMobile ? '32px' : '36px',
              }}
              size={isMobile ? "small" : "medium"}
              clickable
            />
          ))}
        </Box>

        {/* FAQ Questions from PDF */}
        <Typography 
          variant={isMobile ? "subtitle1" : "h6"} 
          sx={{ 
            color: PRIMARY_COLOR, 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: isMobile ? '1rem' : '1.25rem',
          }}
        >
          {category.icon}
          {isMobile ? `${category.name.split(' ')[0]} FAQs` : `${category.name} FAQs`}
        </Typography>
        
        <Box sx={{ 
          maxHeight: isMobile ? '200px' : '250px', 
          overflowY: "auto", 
          mb: 3,
          pr: 1,
        }}>
          {category.questions.map((faq, index) => (
            <Accordion 
              key={faq.id} 
              elevation={0}
              sx={{ 
                mb: 1,
                backgroundColor: `${SECONDARY_COLOR}10`,
                border: `1px solid ${SECONDARY_COLOR}30`,
                borderRadius: '8px !important',
                '&:before': {
                  display: 'none',
                },
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon sx={{ color: PRIMARY_COLOR }} />}
                sx={{
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                  },
                  minHeight: '48px !important',
                  '&.Mui-expanded': {
                    minHeight: '48px !important',
                  },
                }}
              >
                <Typography 
                  variant="body2" 
                  fontWeight={500} 
                  sx={{ 
                    color: PRIMARY_COLOR,
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                  }}
                >
                  <strong>Q{index + 1}:</strong> {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: TEXT_COLOR, 
                    pl: 2,
                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                  }}
                >
                  <strong>A:</strong> {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* FAQ Contact Form Section */}
        <Box sx={{ 
          mb: 3,
          p: isMobile ? 1.5 : 2,
          borderRadius: 2,
          backgroundColor: `${PRIMARY_COLOR}05`,
          border: `1px solid ${SECONDARY_COLOR}30`,
        }}>
          <Typography 
            variant="subtitle1" 
            fontWeight={600}
            sx={{ 
              color: PRIMARY_COLOR, 
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <QuestionAnswerIcon />
            Have More Questions?
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: TEXT_COLOR, 
              mb: 3,
              fontSize: isMobile ? '0.85rem' : '0.875rem',
            }}
          >
            Still have questions about {category.name}? Ask us directly and we'll get back to you!
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Name Field */}
            <TextField
              fullWidth
              size="small"
              label="Your Name"
              value={faqContactForm.name}
              onChange={(e) => setFaqContactForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter your full name"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: SECONDARY_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: PRIMARY_COLOR,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Email Field */}
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Your Email"
              value={faqContactForm.email}
              onChange={(e) => setFaqContactForm(prev => ({ ...prev, email: e.target.value }))}
              placeholder="Enter your email address"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: SECONDARY_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: PRIMARY_COLOR,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Phone Field */}
            <TextField
              fullWidth
              size="small"
              type="tel"
              label="Your Phone Number"
              value={faqContactForm.phone}
              onChange={(e) => setFaqContactForm(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Enter your phone number"
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: SECONDARY_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: PRIMARY_COLOR,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Question Field */}
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Your Question"
              value={faqContactForm.question}
              onChange={(e) => setFaqContactForm(prev => ({ ...prev, question: e.target.value }))}
              placeholder="Type your question here..."
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: SECONDARY_COLOR,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: PRIMARY_COLOR,
                  },
                },
              }}
            />

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleFaqContactSubmit}
              disabled={isFaqSubmitting}
              sx={{
                backgroundColor: `${PRIMARY_COLOR} !important`,
                color: 'white',
                '&:hover': {
                  backgroundColor: `${PRIMARY_COLOR}cc !important`,
                },
                '&.Mui-disabled': {
                  backgroundColor: `${PRIMARY_COLOR}40 !important`,
                },
                height: isMobile ? '40px' : '44px',
                fontSize: isMobile ? '0.875rem' : '1rem',
                fontWeight: 600,
                mt: 1,
              }}
            >
              {isFaqSubmitting ? 'Submitting...' : 'Submit Your Question'}
            </Button>
          </Box>
        </Box>

        {/* Get Consultation Button */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<DesignIcon />}
          onClick={() => {
            setMode("consultation");
            setSelectedProjectType("");
            setCurrentQuestions([]);
            setAnswers({});
            setTouchedFields({});
          }}
          sx={{ 
            mt: 2,
            backgroundColor: `${PRIMARY_COLOR} !important`,
            color: 'white',
            '&:hover': {
              backgroundColor: `${PRIMARY_COLOR}cc !important`,
            },
            height: isMobile ? '40px' : '44px',
            fontSize: isMobile ? '0.875rem' : '1rem',
          }}
        >
          Get Free Consultation
        </Button>
      </Box>
    );
  };

  const renderProjectTypeSelector = () => (
    <Box sx={{ p: isMobile ? 1.5 : 2 }}>
      <Typography 
        variant={isMobile ? "subtitle1" : "h6"} 
        sx={{ color: PRIMARY_COLOR, mb: 2 }}
      >
        Select Your Project Type
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: TEXT_COLOR, 
          mb: 3,
          fontSize: isMobile ? '0.85rem' : '0.875rem',
        }}
      >
        Tell us about your project so we can provide the best consultation for your needs.
      </Typography>
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel 
          sx={{ 
            color: PRIMARY_COLOR,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}
        >
          Project Type
        </InputLabel>
        <Select
          value={selectedProjectType}
          onChange={(e) => setSelectedProjectType(e.target.value)}
          label="Project Type"
          sx={{
            color: PRIMARY_COLOR,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: SECONDARY_COLOR,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: PRIMARY_COLOR,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: PRIMARY_COLOR,
            },
            height: isMobile ? '48px' : '56px',
          }}
          IconComponent={ArrowDropDownIcon}
        >
          {projectTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ color: PRIMARY_COLOR }}>
                  {type.icon}
                </Box>
                <Typography sx={{ 
                  color: PRIMARY_COLOR,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}>
                  {type.label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography 
        variant="body2" 
        sx={{ 
          color: TEXT_COLOR, 
          mb: 2, 
          fontStyle: 'italic',
          fontSize: isMobile ? '0.85rem' : '0.875rem',
        }}
      >
        We'll ask specific questions based on your project type to provide the best consultation.
      </Typography>
    </Box>
  );

  const renderQuestionForm = () => {
    if (currentQuestions.length === 0) return null;
    
    // Calculate progress
    const answeredQuestions = currentQuestions.filter(q => {
      const answer = answers[q.id];
      return answer && (!Array.isArray(answer) || answer.length > 0);
    }).length;
    const progressPercentage = (answeredQuestions / currentQuestions.length) * 100;

    return (
      <Box sx={{ p: isMobile ? 1.5 : 2 }}>
        {/* Project Type Info */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          borderRadius: 2, 
          backgroundColor: `${PRIMARY_COLOR}10`,
          borderLeft: `4px solid ${SECONDARY_COLOR}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Avatar sx={{ 
              bgcolor: SECONDARY_COLOR,
              color: PRIMARY_COLOR,
              width: 24,
              height: 24,
            }}>
              {projectTypes.find(p => p.value === selectedProjectType)?.icon || <DesignIcon />}
            </Avatar>
            <Typography 
              variant="subtitle1" 
              fontWeight={600}
              sx={{ color: PRIMARY_COLOR }}
            >
              {projectTypes.find(p => p.value === selectedProjectType)?.label || selectedProjectType}
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: TEXT_COLOR,
              fontSize: isMobile ? '0.85rem' : '0.875rem',
            }}
          >
            Please fill in the details below for your consultation request. We'll send an email confirmation.
          </Typography>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: `${SECONDARY_COLOR}30`,
              '& .MuiLinearProgress-bar': {
                backgroundColor: PRIMARY_COLOR,
              },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: TEXT_COLOR,
                fontSize: isMobile ? '0.75rem' : '0.8rem',
              }}
            >
              {answeredQuestions} of {currentQuestions.length} questions answered
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: PRIMARY_COLOR, 
                fontWeight: 600,
                fontSize: isMobile ? '0.75rem' : '0.8rem',
              }}
            >
              {Math.round(progressPercentage)}%
            </Typography>
          </Box>
        </Box>

        {/* All Questions in One Form - Scrollable */}
        <Box sx={{ 
          maxHeight: isMobile ? '350px' : '400px',
          overflowY: 'auto',
          pr: 1,
          mb: 3,
        }}>
          {currentQuestions.map((question, index) => (
            <FormSection key={question.id} elevation={0} sx={{ mb: 2 }}>
              {renderQuestionInput(question)}
            </FormSection>
          ))}
        </Box>

        {/* Submit Button */}
        <Button
          fullWidth
          variant="contained"
          startIcon={<CheckIcon />}
          onClick={handleSubmitForm}
          disabled={isSubmitting}
          sx={{
            backgroundColor: `${PRIMARY_COLOR} !important`,
            color: 'white',
            '&:hover': {
              backgroundColor: `${PRIMARY_COLOR}cc !important`,
            },
            '&.Mui-disabled': {
              backgroundColor: `${PRIMARY_COLOR}40 !important`,
            },
            height: isMobile ? '44px' : '48px',
            fontSize: isMobile ? '0.9rem' : '1rem',
            fontWeight: 600,
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Consultation Request'}
        </Button>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            setSelectedProjectType("");
            setCurrentQuestions([]);
            setAnswers({});
            setTouchedFields({});
          }}
          sx={{
            mt: 1,
            color: PRIMARY_COLOR,
            borderColor: PRIMARY_COLOR,
            '&:hover': {
              borderColor: PRIMARY_COLOR,
              backgroundColor: `${PRIMARY_COLOR}10`,
            },
            height: isMobile ? '40px' : '44px',
            fontSize: isMobile ? '0.85rem' : '0.9rem',
          }}
        >
          Back to Project Selection
        </Button>
      </Box>
    );
  };

  const shouldShowPreview = showPreview && !isOpen && !isMobile;

  return (
    <>
      <StyledChatContainer>
        <Collapse in={isOpen} orientation="vertical">
          <StyledChatBox elevation={3}>
            {/* Header */}
            <ChatHeader>
              <Box display="flex" alignItems="center" gap={1}>
                <Avatar
                  sx={{
                    bgcolor: SECONDARY_COLOR,
                    color: PRIMARY_COLOR,
                    width: isMobile ? 28 : 32,
                    height: isMobile ? 28 : 32,
                    fontWeight: 'bold',
                    fontSize: isMobile ? '0.9rem' : '1rem',
                  }}
                >
                  <DesignIcon fontSize={isMobile ? "small" : "medium"} />
                </Avatar>
                <Box>
                  <Typography 
                    variant={isMobile ? "subtitle1" : "h6"}
                    sx={{ fontSize: isMobile ? '0.95rem' : '1.25rem' }}
                  >
                    Design Assistant
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ 
                      opacity: 0.9, 
                      display: "block",
                      color: SECONDARY_COLOR,
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                    }}
                  >
                    {mode === "faq" 
                      ? "Browse FAQs" 
                      : mode === "consultation" 
                        ? "Free Consultation"
                        : "Design Assistant"}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size={isMobile ? "small" : "medium"}
                onClick={() => dispatch(toggleChat())}
                sx={{ 
                  color: SECONDARY_COLOR,
                  '&:hover': {
                    backgroundColor: `${SECONDARY_COLOR}20`,
                  },
                  padding: isMobile ? '4px' : '8px',
                }}
              >
                <CloseIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </ChatHeader>

            {/* Mode Selector */}
            {!selectedProjectType && currentQuestions.length === 0 && (
              <Box sx={{ 
                p: isMobile ? 0.75 : 1, 
                borderBottom: 1, 
                borderColor: `${SECONDARY_COLOR}40`, 
                display: "flex", 
                gap: 0.75,
                backgroundColor: `${SECONDARY_COLOR}10`,
              }}>
                <Button
                  size={isMobile ? "small" : "medium"}
                  variant={mode === "faq" ? "contained" : "outlined"}
                  startIcon={<QuestionAnswerIcon />}
                  onClick={() => setMode("faq")}
                  fullWidth
                  sx={{
                    backgroundColor: mode === "faq" 
                      ? `${PRIMARY_COLOR} !important`
                      : 'transparent',
                    color: mode === "faq" 
                      ? 'white' 
                      : PRIMARY_COLOR,
                    borderColor: PRIMARY_COLOR,
                    '&:hover': {
                      backgroundColor: mode === "faq"
                        ? `${PRIMARY_COLOR}cc !important`
                        : `${PRIMARY_COLOR}10 !important`,
                      borderColor: PRIMARY_COLOR,
                    },
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    minHeight: isMobile ? '36px' : '40px',
                  }}
                >
                  {isMobile ? 'FAQs' : 'Browse FAQs'}
                </Button>
                <Button
                  size={isMobile ? "small" : "medium"}
                  variant={mode === "consultation" ? "contained" : "outlined"}
                  startIcon={<DesignIcon />}
                  onClick={() => {
                    setMode("consultation");
                    setSelectedProjectType("");
                    setCurrentQuestions([]);
                    setAnswers({});
                    setTouchedFields({});
                  }}
                  fullWidth
                  sx={{
                    backgroundColor: mode === "consultation" 
                      ? `${PRIMARY_COLOR} !important`
                      : 'transparent',
                    color: mode === "consultation" 
                      ? 'white' 
                      : PRIMARY_COLOR,
                    borderColor: PRIMARY_COLOR,
                    '&:hover': {
                      backgroundColor: mode === "consultation"
                        ? `${PRIMARY_COLOR}cc !important`
                        : `${PRIMARY_COLOR}10 !important`,
                      borderColor: PRIMARY_COLOR,
                    },
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    minHeight: isMobile ? '36px' : '40px',
                  }}
                >
                  {isMobile ? 'Consult' : 'Free Consultation'}
                </Button>
              </Box>
            )}

            {/* Messages Area */}
            <MessageList>
              {mode === "faq" ? (
                // FAQ Mode - Show FAQs from PDF with contact form
                renderFaqSection()
              ) : mode === "consultation" ? (
                // Free Consultation Mode
                <>
                  {/* Welcome message */}
                  {!selectedProjectType && currentQuestions.length === 0 && (
                    <ListItem sx={{ 
                      justifyContent: "flex-start", 
                      alignItems: "flex-start",
                      px: isMobile ? 1 : 2,
                    }}>
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: PRIMARY_COLOR, 
                          color: SECONDARY_COLOR,
                          width: isMobile ? 28 : 32, 
                          height: isMobile ? 28 : 32,
                        }}>
                          <DesignIcon fontSize="small" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ ml: isMobile ? 1 : 2 }}
                        primary={
                          <Typography sx={{
                            bgcolor: `${SECONDARY_COLOR}15`,
                            p: isMobile ? 1 : 1.5,
                            borderRadius: 2,
                            borderTopLeftRadius: 0,
                            maxWidth: "90%",
                            wordBreak: "break-word",
                            borderLeft: `3px solid ${PRIMARY_COLOR}`,
                            color: PRIMARY_COLOR,
                            fontSize: isMobile ? '0.9rem' : '1rem',
                          }}>
                            Welcome to Free Consultation!
                            <br />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: TEXT_COLOR, 
                                display: 'block', 
                                mt: 1,
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                              }}
                            >
                              {BRAND_PHILOSOPHY}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: TEXT_COLOR, 
                                display: 'block', 
                                mt: 1,
                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                              }}
                            >
                              Let's understand your project requirements to provide the best design solution. We'll send you an email confirmation.
                            </Typography>
                          </Typography>
                        }
                      />
                    </ListItem>
                  )}

                  {/* Project Type Selector */}
                  {mode === "consultation" && !selectedProjectType && renderProjectTypeSelector()}

                  {/* Question Form */}
                  {selectedProjectType && renderQuestionForm()}

                  <div ref={messagesEndRef} />
                </>
              ) : null}
            </MessageList>

            {/* Submission History & Actions */}
            {!selectedProjectType && mode === "consultation" && currentQuestions.length === 0 && (
              <Box sx={{ 
                p: isMobile ? 1.5 : 2, 
                borderTop: 1, 
                borderColor: `${SECONDARY_COLOR}40`,
                backgroundColor: `${SECONDARY_COLOR}10`,
              }}>
                {submissions.length > 0 ? (
                  <>
                    <Typography 
                      variant="subtitle2" 
                      fontWeight={600} 
                      gutterBottom
                      sx={{ 
                        color: PRIMARY_COLOR,
                        fontSize: isMobile ? '0.85rem' : '0.875rem',
                      }}
                    >
                      📋 Previous Consultations ({submissions.length})
                    </Typography>
                    <Box sx={{ 
                      maxHeight: isMobile ? '80px' : '100px', 
                      overflowY: "auto", 
                      mb: 2,
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 0.5,
                    }}>
                      {submissions.slice(-3).reverse().map((sub, idx) => (
                        <Chip
                          key={sub.id}
                          label={`${new Date(sub.timestamp).toLocaleDateString()} - ${sub.formData?.project_type_label || "Project"}`}
                          size="small"
                          sx={{ 
                            backgroundColor: `${PRIMARY_COLOR}15 !important`,
                            color: PRIMARY_COLOR,
                            borderColor: PRIMARY_COLOR,
                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                            height: isMobile ? '28px' : '32px',
                          }}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                    <Box sx={{ 
                      display: "flex", 
                      gap: 1,
                      flexDirection: isMobile ? 'column' : 'row',
                    }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => {
                          setSelectedProjectType("");
                          setCurrentQuestions([]);
                          setAnswers({});
                          setTouchedFields({});
                        }}
                        startIcon={<DesignIcon />}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          backgroundColor: `${PRIMARY_COLOR} !important`,
                          color: 'white',
                          '&:hover': {
                            backgroundColor: `${PRIMARY_COLOR}cc !important`,
                          },
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          height: isMobile ? '36px' : '40px',
                        }}
                      >
                        Start New Consultation
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleClearData}
                        startIcon={<CloseIcon />}
                        size={isMobile ? "small" : "medium"}
                        sx={{
                          color: ERROR_COLOR,
                          borderColor: ERROR_COLOR,
                          '&:hover': {
                            borderColor: ERROR_COLOR,
                            backgroundColor: `${ERROR_COLOR}10`,
                          },
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          height: isMobile ? '36px' : '40px',
                          width: isMobile ? '100%' : 'auto',
                        }}
                      >
                        Clear All
                      </Button>
                    </Box>
                  </>
                ) : mode === "consultation" && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: TEXT_COLOR, 
                      textAlign: 'center', 
                      mb: 2,
                      fontSize: isMobile ? '0.85rem' : '0.875rem',
                    }}
                  >
                    Select a project type above to start your free consultation
                  </Typography>
                )}
              </Box>
            )}
          </StyledChatBox>
        </Collapse>

        {/* Chat Preview - Positioned beside the Chat Icon */}
        {shouldShowPreview && (
          <Box sx={{
            position: "absolute",
            bottom: 8,
            right: 65,
            display: "flex",
            alignItems: "center",
            zIndex: 999,
            animation: `${fadeIn} 0.3s ease-out`,
            [theme.breakpoints.down('md')]: {
              right: 120,
            },
          }}>
            <Paper sx={{
              p: 1.5,
              pr: 2.5,
              borderRadius: "12px 12px 12px 4px",
              boxShadow: `0 2px 12px ${PRIMARY_COLOR}20`,
              bgcolor: PRIMARY_COLOR,
              border: `1px solid ${SECONDARY_COLOR}`,
              maxWidth: 220,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 8,
                right: 58,
                width: 0,
                height: 0,
                borderTop: "8px solid transparent",
                borderBottom: "8px solid transparent",
                borderLeft: `8px solid ${PRIMARY_COLOR}`,
              },
            }}>
              <Typography variant="body2" sx={{ 
                fontSize: "0.875rem", 
                lineHeight: 1.4,
                color: SECONDARY_COLOR,
                fontWeight: 500,
              }}>
                Ready to transform your space? Let's chat!
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Floating Chat Button */}
        <Tooltip title="Chat with Design Assistant">
          <Fab
            color="primary"
            onClick={() => {
              dispatch(toggleChat());
              setShowPreview(false);
              setMode("faq");
              setSelectedProjectType("");
              setCurrentQuestions([]);
              setAnswers({});
              setTouchedFields({});
            }}
            sx={{
              mt: 2,
              ml: 40,
              position: "relative",
              width: isMobile ? 48 : 56,
              height: isMobile ? 48 : 56,
              bgcolor: `${PRIMARY_COLOR} !important`,
              "&:hover": {
                bgcolor: `${PRIMARY_COLOR}cc !important`,
              },
              boxShadow: `0 4px 12px ${PRIMARY_COLOR}40`,
              "&:active": {
                boxShadow: `0 2px 6px ${PRIMARY_COLOR}60`,
              },
              animation: isOpen ? "none" : "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: `0 0 0 0 ${PRIMARY_COLOR}70` },
                "70%": { boxShadow: `0 0 0 10px ${PRIMARY_COLOR}00` },
                "100%": { boxShadow: `0 0 0 0 ${PRIMARY_COLOR}00` },
              },
              [theme.breakpoints.down('sm')]: {
                ml: 0,
                mt: 0,
                position: 'fixed',
                bottom: theme.spacing(1),
                right: theme.spacing(1),
              },
              [theme.breakpoints.up('sm')]: {
                position: 'fixed',
                bottom: theme.spacing(2),
                right: theme.spacing(2),
                ml: 0,
                mt: 0,
              },
            }}
          >
            <Badge
              badgeContent={submissions.length}
              color="error"
              invisible={submissions.length === 0}
              max={99}
              sx={{
                "& .MuiBadge-badge": {
                  right: isMobile ? 4 : 8,
                  top: isMobile ? 4 : 8,
                  border: "2px solid white",
                  bgcolor: SECONDARY_COLOR,
                  color: PRIMARY_COLOR,
                  fontWeight: "bold",
                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                  minWidth: isMobile ? '18px' : '20px',
                  height: isMobile ? '18px' : '20px',
                },
              }}
            >
              <ChatIcon sx={{ 
                color: SECONDARY_COLOR, 
                fontSize: isMobile ? 24 : 28 
              }} />
            </Badge>
          </Fab>
        </Tooltip>
      </StyledChatContainer>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ 
          vertical: isMobile ? "top" : "bottom", 
          horizontal: isMobile ? "center" : "right" 
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: "100%",
            '&.MuiAlert-standardSuccess': {
              backgroundColor: SECONDARY_COLOR,
              color: PRIMARY_COLOR,
            },
            '&.MuiAlert-standardInfo': {
              backgroundColor: `${PRIMARY_COLOR}20`,
              color: PRIMARY_COLOR,
            },
            '&.MuiAlert-standardWarning': {
              backgroundColor: '#ff9800',
              color: 'white',
            },
            '&.MuiAlert-standardError': {
              backgroundColor: ERROR_COLOR,
              color: 'white',
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}